import clsx from "clsx"
import * as React from "react"

import clsxm from "@/lib/clsxm"

import Heading from "@/components/common/Heading/Heading"
import Icon from "@/components/common/Icon/Icon"
import NextImage from "@/components/common/NextImage/NextImage"

import {
  CalendarSuccessIcon,
  CreditCardIcon,
  PersonSuccessIcon,
  SearchIcon,
} from "@/constants/icons"

import howItWorksImage from "../../../public/images/homepage/how-it-works.png"
import HowItWorksDivider from "../../../public/svg/homepage/how-it-works-divider.svg"

const HowItWorks = () => {
  return (
    <section
      className={clsxm(
        `relative flex flex-col items-center justify-center bg-mint `,
        `md:pb-20`
      )}
    >
      <HowItWorksDivider
        className={clsx(
          `-mt-px h-auto w-full`,
          `sm:absolute sm:left-0 sm:-top-1 sm:max-h-full`
        )}
      />
      <div
        className={clsxm(
          `relative z-10 w-full max-w-7xl px-4`,
          `sm:grid sm:grid-cols-12 sm:justify-between`
        )}
      >
        <div
          className={clsx(
            `relative col-span-6 hidden`,
            `md:col-span-3 md:block`,
            `lg:col-span-4`,
            `xl:col-span-5`
          )}
        >
          <NextImage
            src={howItWorksImage}
            alt={`Person at appointment for physio.`}
            className={`absolute right-12 top-1/2 z-10 ml-auto h-auto w-full min-w-[497px] -translate-y-1/2`}
          />
        </div>
        <div
          className={clsx(
            `py-8 pb-20`,
            `sm:col-span-12`,
            `md:col-span-8`,
            `lg:col-span-7`,
            `xl:col-span-6 xl:col-start-7`
          )}
        >
          <Heading
            className={clsx(
              `mb-4 block font-baskervilleHeadings text-4xl tracking-tight`,
              `sm:hidden`
            )}
            headingLevel={`h2`}
          >
            How it works
          </Heading>
          <div
            className={clsx(
              `grid gap-4`,
              `sm:auto-cols-fr sm:auto-rows-fr sm:grid-cols-2 sm:gap-8`
            )}
          >
            <Card
              heading={`Find your practitioner`}
              bodyParagraph={`Browse available days and times seven days out.`}
              icon={SearchIcon}
            />
            <Card
              heading={`Fit to your schedule`}
              bodyParagraph={`Choose an appointment time that works for you.`}
              icon={CalendarSuccessIcon}
            />
            <Card
              heading={`Upfront payment`}
              bodyParagraph={`Guarantee your last minute spot with payment upfront.`}
              icon={CreditCardIcon}
            />
            <Card
              heading={`Enjoy your treatment`}
              bodyParagraph={`All that’s left to do is show up to your appointment and enjoy.`}
              icon={PersonSuccessIcon}
            />
          </div>
        </div>
      </div>
    </section>
  )
}

type CardProps = {
  heading: string
  bodyParagraph: string
  icon: React.ElementType
}

function Card({ heading, bodyParagraph, icon }: CardProps) {
  return (
    <div
      className={clsxm(
        `flex flex-row items-start justify-start space-x-6  rounded-2xl border border-gray-50 bg-white px-6 py-8 shadow-[0px_16px_32px_-4px_rgba(0,0,0,0.05)]`,
        `sm:flex-col sm:items-start sm:justify-end sm:space-x-0 sm:space-y-6 sm:py-12 sm:px-8`,
        `md:even:mt-20 md:even:-mb-20`
      )}
    >
      <div
        className={clsx(
          `flex h-12 w-12 flex-auto flex-shrink-0 flex-grow-0 items-center justify-center rounded-lg border border-primary-300 bg-primary-50 text-primary-500`,
          `sm:h-[72px] sm:w-[72px] sm:flex-grow-0 sm:rounded-2xl`
        )}
      >
        <Icon size={`lg-32`} icon={icon} />
      </div>
      <div className={clsx(`flex-auto`, `sm:flex-shrink-1 sm:flex-grow-0`)}>
        <p className={`text-lg font-bold`}>{heading}</p>
        <p className={`text-lg`}>{bodyParagraph}</p>
      </div>
    </div>
  )
}

export default HowItWorks
